import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Pencil, Trash2, Eye, Check, X, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { apiRequest } from '../../lib/utils';
import { BLOG_STATUS_STYLES, formatBlogDate } from '../../constants/blog';

const FILTERS = ['all', 'pending', 'published', 'draft', 'rejected'];

const StatusPill = ({ status }) => {
  const s = BLOG_STATUS_STYLES[status] || BLOG_STATUS_STYLES.draft;
  return <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${s.className}`}>{s.label}</span>;
};

const BlogRow = ({ blog, isAdmin, onDelete, onReview }) => (
  <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 border border-slate-100 rounded-xl hover:bg-slate-50/60 transition-colors" data-testid={`blog-row-${blog.id}`}>
    <div className="w-full sm:w-24 h-32 sm:h-16 rounded-lg bg-slate-100 overflow-hidden flex-shrink-0 flex items-center justify-center">
      {blog.cover_image ? <img src={blog.cover_image} alt="" className="w-full h-full object-cover" /> : <FileText className="w-6 h-6 text-slate-300" />}
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex flex-wrap items-center gap-2">
        <p className="font-semibold text-slate-900 truncate">{blog.title}</p>
        <StatusPill status={blog.status} />
      </div>
      <p className="text-xs text-slate-500 mt-1">
        {isAdmin && <>{blog.author_name || 'Unknown'} ({blog.author_role}) · </>}
        {blog.category || 'Uncategorised'} · Updated {formatBlogDate(blog.updated_at)}
      </p>
      {blog.status === 'rejected' && blog.rejection_reason && (
        <p className="text-xs text-red-600 mt-1" data-testid={`blog-reject-reason-${blog.id}`}>Reason: {blog.rejection_reason}</p>
      )}
    </div>
    <div className="flex flex-wrap gap-2">
      {isAdmin && blog.status === 'pending' && (
        <>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => onReview(blog, true)} data-testid={`blog-approve-${blog.id}`}><Check className="w-4 h-4 mr-1" />Approve</Button>
          <Button size="sm" variant="outline" className="border-red-200 text-red-700" onClick={() => onReview(blog, false)} data-testid={`blog-reject-${blog.id}`}><X className="w-4 h-4 mr-1" />Reject</Button>
        </>
      )}
      {blog.status === 'published' && (
        <Button size="sm" variant="outline" asChild><Link to={`/blog/${blog.slug}`} target="_blank" data-testid={`blog-view-${blog.id}`}><Eye className="w-4 h-4" /></Link></Button>
      )}
      <Button size="sm" variant="outline" asChild><Link to={`/blog-editor/${blog.id}`} data-testid={`blog-edit-${blog.id}`}><Pencil className="w-4 h-4" /></Link></Button>
      <Button size="sm" variant="outline" className="text-red-600" onClick={() => onDelete(blog)} data-testid={`blog-delete-${blog.id}`}><Trash2 className="w-4 h-4" /></Button>
    </div>
  </div>
);

export const BlogManager = ({ isAdmin = false }) => {
  const [blogs, setBlogs] = useState([]);
  const [filter, setFilter] = useState(isAdmin ? 'pending' : 'all');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const data = await apiRequest(`/my/blogs${filter !== 'all' ? `?status=${filter}` : ''}`);
      setBlogs(data.blogs);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter]);

  const onDelete = async (blog) => {
    if (!window.confirm(`Delete "${blog.title}"? This cannot be undone.`)) return;
    try {
      await apiRequest(`/my/blogs/${blog.id}`, { method: 'DELETE' });
      toast.success('Blog deleted');
      load();
    } catch (e) { toast.error(e.message); }
  };

  const onReview = async (blog, approve) => {
    const reason = approve ? null : window.prompt('What should the author change? (shown to the author)');
    if (!approve && reason === null) return;
    try {
      await apiRequest(`/admin/blogs/${blog.id}/review`, { method: 'POST', body: JSON.stringify({ approve, reason }) });
      toast.success(approve ? 'Blog published' : 'Sent back to author');
      load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <Card data-testid="blog-manager">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
        <div>
          <CardTitle>{isAdmin ? 'Blogs' : 'My Blogs'}</CardTitle>
          <p className="text-sm text-slate-500 mt-1">
            {isAdmin ? 'Write posts, and approve blogs submitted by doctors.' : 'Write health articles. They go live after admin approval.'}
          </p>
        </div>
        <Button asChild className="bg-[#3B1C73] hover:bg-[#2c1457] text-white">
          <Link to="/blog-editor" data-testid="new-blog-btn"><Plus className="w-4 h-4 mr-1" />Write new blog</Link>
        </Button>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-2 mb-4">
          {FILTERS.map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`blog-filter-${f}`}
              className={`px-3 py-1 rounded-full text-sm capitalize transition-colors ${filter === f ? 'bg-[#3B1C73] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
              {f === 'all' ? 'All' : BLOG_STATUS_STYLES[f].label}
            </button>
          ))}
        </div>
        {loading ? (
          <div className="py-10 flex justify-center"><div className="spinner" /></div>
        ) : blogs.length === 0 ? (
          <p className="text-center text-slate-500 py-10" data-testid="blog-empty">No blogs here yet.</p>
        ) : (
          <div className="space-y-3">
            {blogs.map((b) => <BlogRow key={b.id} blog={b} isAdmin={isAdmin} onDelete={onDelete} onReview={onReview} />)}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
